import type {
  Conditions,
  Token,
} from '@risksmart-app/form-configuration/src/field-types/types';
import type { ModuleConfig } from '@risksmart-app/modules/src/index';
import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import { getFormConfigRegistry } from '@risksmart-app/shared/forms/formConfigRegistry';
import type {
  DefaultFieldCondition,
  FormFieldConfig,
} from '@risksmart-app/shared/forms/types';
import type { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FormEditorPreviewData } from 'src/blocks/form-editor-dialog';
import type { SectionEditorValues } from 'src/blocks/form-editor-dialog/config';

import {
  buildStandardFieldCanonicalMap,
  canonicaliseStandardFieldKey,
  remapConditionPropertyKeys,
  scopeToServerId,
} from './conditionPropertyKeys';
import type {
  CustomisableFormDataContextState,
  TypedFormFieldConfiguration,
} from './customisable-form-data/CustomisableFormDataContext';

export interface EditorConditionalLogicState {
  enabled: boolean;
  operation: 'and' | 'or';
  rules: { ifField: string; operator: string; values: string[] }[];
}

/**
 * Inverse of `convertEditorRulesToConditions`: hydrates the editor-side
 * representation from a persisted Conditions value. The editor only supports a
 * flat list of `field = value(s)` rules, so OR-tokenGroups whose tokens
 * reference different fields are dropped (with a warning) since they can't be
 * round-tripped through the simplified UI.
 */
export const convertConditionsToEditorRules = (
  conditions: Conditions | null | undefined
): EditorConditionalLogicState => {
  if (!conditions || conditions.tokenGroups.length === 0) {
    return { enabled: false, operation: 'and', rules: [] };
  }

  const rules: { ifField: string; operator: string; values: string[] }[] = [];
  for (const group of conditions.tokenGroups) {
    if ('operation' in group) {
      if (group.tokens.length === 0) {
        continue;
      }
      const propertyKey = group.tokens[0].propertyKey;
      const allSameField = group.tokens.every(
        (t) => t.propertyKey === propertyKey
      );
      if (!allSameField) {
        // Mixed-property OR groups can't be expressed in the simplified
        // editor UI; tracked under RSP-5562/5563. No production data
        // currently produces this shape.
        continue;
      }
      rules.push({
        ifField: propertyKey,
        operator: group.tokens[0].operator,
        values: group.tokens.map((t) => String(t.value)),
      });
    } else {
      rules.push({
        ifField: group.propertyKey,
        operator: group.operator,
        values: [String(group.value)],
      });
    }
  }

  return { enabled: rules.length > 0, operation: conditions.operation, rules };
};

/**
 * Converts a single editor conditional logic rule set into the Conditions type
 * used by the form customisation system.
 *
 * Locked rules (code-declared read-only defaults surfaced in merge mode) are
 * stripped: they stay code-resident and are never persisted. Only customer
 * (unlocked) rules are converted, so a field with locked rules but no customer
 * rules yields `null` (no row promotion) — the runtime re-applies the locked
 * default via `getEffectiveFieldConditions`.
 */
export const convertEditorRulesToConditions = (
  rules:
    | {
        ifField: string;
        operator?: string;
        values: string[];
        locked?: boolean;
      }[]
    | undefined,
  enabled: boolean,
  operation?: 'and' | 'or'
): Conditions | null => {
  const customerRules = rules?.filter((rule) => !rule.locked);
  if (!enabled || !customerRules || customerRules.length === 0) {
    return null;
  }

  const tokenGroups: Conditions['tokenGroups'] = customerRules.map((rule) => {
    const operator = (rule.operator ?? '=') as Token['operator'];
    if (rule.values.length === 1) {
      return {
        value: rule.values[0] as Required<unknown>,
        propertyKey: rule.ifField,
        operator,
      };
    }

    return {
      operation: 'or' as const,
      tokens: rule.values.map((value) => ({
        value: value as Required<unknown>,
        propertyKey: rule.ifField,
        operator,
      })),
    };
  });

  return {
    operation: operation ?? 'and',
    tokens: [],
    tokenGroups,
  };
};

/**
 * Synthesises a persisted-shaped `Conditions` from a code-declared
 * `defaultConditions` descriptor. Produces the same token structure as
 * `convertEditorRulesToConditions` for `=` rules, so a materialised editable
 * default is byte-identical to the code default it came from. `ifField` is left
 * in canonical form (the runtime form-value key); the editor-seeding path runs
 * the result back through `convertConditionsToEditorRules` + the editor's
 * id-remap, exactly like a persisted condition.
 */
export const defaultFieldConditionToConditions = (
  defaultCondition: DefaultFieldCondition
): Conditions | null => {
  if (!defaultCondition.rules.length) {
    return null;
  }

  const tokenGroups: Conditions['tokenGroups'] = defaultCondition.rules.map(
    (rule): Conditions['tokenGroups'][number] => {
      const operator = rule.operator ?? '=';
      if (rule.values.length === 1) {
        return {
          value: rule.values[0] as Required<unknown>,
          propertyKey: rule.ifField,
          operator,
        };
      }

      return {
        operation: 'or' as const,
        tokens: rule.values.map((value) => ({
          value: value as Required<unknown>,
          propertyKey: rule.ifField,
          operator,
        })),
      };
    }
  );

  return {
    operation: 'and' as const,
    tokens: [],
    tokenGroups,
  };
};

/**
 * Live-preview variant of the editor key → form-value key translation.
 * `useFormEditorSave` does the equivalent at save time; both delegate the
 * traversal + shared building blocks to `./conditionPropertyKeys`. The preview
 * keeps in-session temp ids as-is (their value lives at that same top-level key
 * in the preview form), so unknown keys fall through unchanged.
 */
const remapPreviewPropertyKey = (
  key: string,
  standardFieldCanonicalById: Map<string, string>
): string => {
  // Existing custom attribute: editor uses scope (#/properties/X); the
  // preview form's value for it lives at server format (CustomAttributeData.X).
  if (key.startsWith('#/properties/')) {
    return scopeToServerId(key);
  }

  // Standard field: editor uses lowercase React key; form value lives at
  // canonical FieldId. Unrecognised keys (e.g. a sibling new field's temp id)
  // pass through unchanged.
  return canonicaliseStandardFieldKey(key, standardFieldCanonicalById);
};

const remapConditions = (
  conditions: Conditions | null,
  standardFieldCanonicalById: Map<string, string>
): Conditions | null =>
  remapConditionPropertyKeys(conditions, (key) =>
    remapPreviewPropertyKey(key, standardFieldCanonicalById)
  );

/**
 * Builds a formFieldConfigurations entry from editor state, merging with
 * existing DB data where the editor hasn't made changes.
 */
const buildFieldConfiguration = (
  fieldId: string,
  data: FormEditorPreviewData,
  parentType: Parent_Type_Enum,
  existing: TypedFormFieldConfiguration | undefined,
  standardFieldCanonicalById: Map<string, string>
): TypedFormFieldConfiguration => {
  const field = data.fields[fieldId];
  const config = data.fieldConfigs[fieldId];

  if (!config) {
    // No editor config — use existing DB data or sensible defaults
    return (
      existing ?? {
        FieldId: fieldId,
        FormConfigurationParentType: parentType,
        Hidden: false,
        Required: field?.required ?? false,
        ReadOnly: field?.readOnly ?? false,
        DefaultValue: null,
        Label: field?.name ?? null,
        Description: null,
        Conditions: null,
      }
    );
  }

  return {
    FieldId: fieldId,
    FormConfigurationParentType: parentType,
    Hidden: false,
    Required: config.required ?? field?.required ?? false,
    ReadOnly: config.readOnly ?? field?.readOnly ?? false,
    DefaultValue: existing?.DefaultValue ?? null,
    Label: config.fieldName ?? field?.name ?? null,
    Description: config.guidanceEnabled ? (config.guidance ?? null) : null,
    Conditions: remapConditions(
      convertEditorRulesToConditions(
        config.conditionalLogicRules,
        config.conditionalLogicEnabled,
        config.conditionalLogicOperation
      ),
      standardFieldCanonicalById
    ),
  };
};

/**
 * Builds a formSections entry from editor state, merging with existing DB data.
 */
const buildSectionConfiguration = ({
  sectionId,
  index,
  data,
  parentType,
  sectionConfig,
  standardFieldCanonicalById,
}: {
  sectionId: string;
  index: number;
  data: FormEditorPreviewData;
  parentType: Parent_Type_Enum;
  sectionConfig: SectionEditorValues | undefined;
  standardFieldCanonicalById: Map<string, string>;
}) => {
  const fieldIds = data.containers[sectionId] ?? [];

  return {
    FormConfigurationParentType: parentType,
    SectionId: sectionId,
    Label: data.sectionNames[sectionId] ?? sectionId,
    Description: sectionConfig?.description ?? null,
    Conditions: sectionConfig
      ? remapConditions(
          convertEditorRulesToConditions(
            sectionConfig.conditionalLogicRules,
            sectionConfig.conditionalLogicEnabled,
            sectionConfig.conditionalLogicOperation
          ),
          standardFieldCanonicalById
        )
      : null,
    fields: fieldIds.map((id) => ({ FieldId: id })),
    ordering: { Position: index },
  };
};

/**
 * Converts form editor dialog state into the CustomisableFormDataContextState
 * used by the form rendering pipeline.
 *
 * Merges editor changes with existing DB data — fields/sections the user hasn't
 * modified in the editor retain their original configuration.
 *
 * `formSectionsEnabled` / `modules` are used to look up the form's
 * registered standard fields for propertyKey canonicalisation in conditional
 * logic. Default to `false` / `{}`, safe for tests that don't exercise
 * feature-gated fields.
 */
export const convertEditorStateToFormData = (
  data: FormEditorPreviewData,
  parentType: Parent_Type_Enum,
  existingContext: CustomisableFormDataContextState,
  formSectionsEnabled = false,
  modules: ModuleConfig = {}
): CustomisableFormDataContextState => {
  const existingFieldConfigMap = new Map(
    (existingContext.formFieldConfigurations ?? []).map((fc) => [
      fc.FieldId,
      fc,
    ])
  );

  // Lowercase React-key → canonical FieldId, so condition propertyKeys
  // built from editor input line up with the values RHF watches in the
  // preview form. Mirrors the same map in useFormEditorSave.
  // Parent_Type_Enum is wider than FormId (the registry's keyof) — entries
  // like aggregation_org are not form types, so the indexed access can be
  // undefined and we guard accordingly.
  const formRegistry = getFormConfigRegistry(formSectionsEnabled, modules);
  const formConfig: Record<string, FormFieldConfig> | undefined =
    formRegistry[parentType as FormId];
  const standardFieldCanonicalById = buildStandardFieldCanonicalMap(formConfig);

  // Collect all field IDs across all sections
  const allFieldIds = data.containerOrder.flatMap(
    (id) => data.containers[id] ?? []
  );

  const formFieldConfigurations = allFieldIds.map((fieldId) =>
    buildFieldConfiguration(
      fieldId,
      data,
      parentType,
      existingFieldConfigMap.get(fieldId),
      standardFieldCanonicalById
    )
  );

  const formFieldOrdering = allFieldIds.map((fieldId, index) => ({
    FieldId: fieldId,
    Position: index,
    FormConfigurationParentType: parentType,
  }));

  const formSections = data.containerOrder.map((sectionId, index) =>
    buildSectionConfiguration({
      sectionId,
      index,
      data,
      parentType,
      sectionConfig: data.sectionConfigs[sectionId],
      standardFieldCanonicalById,
    })
  );

  // Carry forward the per-instance Zustand store handles from existingContext
  // so consumers rendered inside LiveFormPreview's nested
  // CustomisableFormDataContext.Provider keep reading from the form's own
  // registry/elements stores rather than falling off the per-instance scope.
  return {
    ...existingContext,
    formFieldConfigurations,
    formFieldOrdering,
    formSections,
  };
};
